import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Textarea } from '../components/Textarea';
import { Select } from '../components/Select';

export const AgentOnboardingPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  // Step 1: Basic Info
  const [fullName, setFullName] = useState('');
  const [agencyName, setAgencyName] = useState('');
  const [title, setTitle] = useState('');
  const [email, setEmail] = useState('sarah.johnson@literary-partners.com');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');

  // Step 2: Agency Affiliation
  const [agencyAffiliation, setAgencyAffiliation] = useState('existing'); // 'existing' or 'independent'
  const [selectedAgency, setSelectedAgency] = useState('');
  const [newAgencyName, setNewAgencyName] = useState('');
  const [agencyWebsite, setAgencyWebsite] = useState('');
  const [yearsWithAgency, setYearsWithAgency] = useState('');

  // Step 3: MSWL (Manuscript Wish List) Setup
  const [primaryGenres, setPrimaryGenres] = useState<string[]>([]);
  const [mswlDescription, setMswlDescription] = useState('');
  const [specificWants, setSpecificWants] = useState('');
  const [dealBreakers, setDealBreakers] = useState('');

  // Step 4: Preferences
  const [responseTimeframe, setResponseTimeframe] = useState('');
  const [queryStatus, setQueryStatus] = useState('open'); // 'open' or 'closed'
  const [submissionGuidelines, setSubmissionGuidelines] = useState('');
  const [acceptingNewClients, setAcceptingNewClients] = useState(true);

  const agencies = [
    'Literary Partners Agency',
    'The Book Group',
    'Sterling Lord Literistic',
    'Writers House',
    'Curtis Brown Ltd.',
    'Janklow & Nesbit Associates',
    'ICM Partners',
    'Other (Add New)',
  ];

  const genres = [
    'Literary Fiction',
    'Commercial Fiction',
    'Mystery/Thriller',
    'Science Fiction',
    'Fantasy',
    'Romance',
    'Historical Fiction',
    'Young Adult',
    'Middle Grade',
    'Horror',
    'Women\'s Fiction',
    'Memoir',
    'Biography',
    'Self-Help',
    'Business',
    'True Crime',
  ];

  const responseTimeframes = [
    { value: '1-2 weeks', label: '1-2 weeks' },
    { value: '2-4 weeks', label: '2-4 weeks' },
    { value: '4-6 weeks', label: '4-6 weeks' },
    { value: '6-8 weeks', label: '6-8 weeks' },
    { value: '8-12 weeks', label: '8-12 weeks' },
    { value: 'No response means no', label: 'No response means no' },
  ];

  const handleGenreToggle = (genre: string) => {
    if (primaryGenres.includes(genre)) {
      setPrimaryGenres(primaryGenres.filter(g => g !== genre));
    } else if (primaryGenres.length < 5) {
      setPrimaryGenres([...primaryGenres, genre]);
    }
  };

  const validateStep = (step: number) => {
    switch (step) {
      case 1:
        if (!fullName.trim()) {
          window.alert('Please enter your full name');
          return false;
        }
        if (!email.trim()) {
          window.alert('Please enter your email address');
          return false;
        }
        return true;
      case 2:
        if (agencyAffiliation === 'existing' && !selectedAgency) {
          window.alert('Please select your agency');
          return false;
        }
        if (agencyAffiliation === 'independent' && !newAgencyName.trim()) {
          window.alert('Please enter your agency name');
          return false;
        }
        return true;
      case 3:
        if (primaryGenres.length === 0) {
          window.alert('Please select at least one genre you represent');
          return false;
        }
        if (!mswlDescription.trim()) {
          window.alert('Please provide a brief description of what you\'re looking for');
          return false;
        }
        return true;
      case 4:
        if (!responseTimeframe) {
          window.alert('Please select your typical response timeframe');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
        window.scrollTo(0, 0);
      } else {
        handleComplete();
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSkip = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleComplete = () => {
    console.log('Agent onboarding completed:', {
      basicInfo: { fullName, agencyName, title, email, phone, website },
      agency: { agencyAffiliation, selectedAgency, newAgencyName, agencyWebsite, yearsWithAgency },
      mswl: { primaryGenres, mswlDescription, specificWants, dealBreakers },
      preferences: { responseTimeframe, queryStatus, submissionGuidelines, acceptingNewClients },
    });
    window.alert('Welcome to Manuscript Match! Your agent profile is now set up.');
    window.location.href = '/agent/dashboard';
  };

  const renderProgressBar = () => (
    <div style={{ marginBottom: '40px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '8px',
      }}>
        {[1, 2, 3, 4].map((step) => (
          <div
            key={step}
            style={{
              flex: 1,
              height: '4px',
              backgroundColor: step <= currentStep ? theme.colors.primary : theme.colors.border,
              marginRight: step < totalSteps ? '8px' : '0',
              borderRadius: theme.borderRadius.small,
              transition: 'background-color 0.3s',
            }}
          />
        ))}
      </div>
      <div style={{
        fontSize: '14px',
        color: theme.colors.textSecondary,
        textAlign: 'center',
      }}>
        Step {currentStep} of {totalSteps}
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div>
      <h2 style={{
        fontSize: '28px',
        fontWeight: '500',
        marginBottom: '8px',
        color: theme.colors.textPrimary,
      }}>
        Basic Information
      </h2>
      <p style={{
        fontSize: '16px',
        color: theme.colors.textSecondary,
        marginBottom: '32px',
      }}>
        Let's start with your professional details
      </p>

      <Card>
        <Input
          type="text"
          label="Full Name *"
          placeholder="Sarah Johnson"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          required
        />

        <div style={{ marginTop: '24px' }}>
          <Input
            type="text"
            label="Professional Title"
            placeholder="Literary Agent, Senior Agent, etc."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div style={{ marginTop: '24px' }}>
          <Input
            type="email"
            label="Professional Email *"
            placeholder="agent@agency.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div style={{ marginTop: '24px' }}>
          <Input
            type="tel"
            label="Phone Number"
            placeholder="(555) 123-4567"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>

        <div style={{ marginTop: '24px' }}>
          <Input
            type="url"
            label="Personal Website"
            placeholder="https://www.yourwebsite.com"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </div>
      </Card>
    </div>
  );

  const renderStep2 = () => (
    <div>
      <h2 style={{
        fontSize: '28px',
        fontWeight: '500',
        marginBottom: '8px',
        color: theme.colors.textPrimary,
      }}>
        Agency Affiliation
      </h2>
      <p style={{
        fontSize: '16px',
        color: theme.colors.textSecondary,
        marginBottom: '32px',
      }}>
        Tell us about your agency or independent practice
      </p>

      <Card style={{ marginBottom: '24px' }}>
        <label style={{
          fontSize: '14px',
          fontWeight: '500',
          color: theme.colors.textPrimary,
          marginBottom: '12px',
          display: 'block',
        }}>
          Agency Status *
        </label>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
        }}>
          <button
            onClick={() => setAgencyAffiliation('existing')}
            style={{
              padding: '20px',
              border: `2px solid ${agencyAffiliation === 'existing' ? theme.colors.primary : theme.colors.border}`,
              borderRadius: theme.borderRadius.medium,
              backgroundColor: agencyAffiliation === 'existing' ? theme.colors.primaryLight : theme.colors.white,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            <div style={{
              fontSize: '16px',
              fontWeight: '500',
              color: theme.colors.textPrimary,
              marginBottom: '4px',
            }}>
              Agency Agent
            </div>
            <div style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
            }}>
              Part of an established agency
            </div>
          </button>

          <button
            onClick={() => setAgencyAffiliation('independent')}
            style={{
              padding: '20px',
              border: `2px solid ${agencyAffiliation === 'independent' ? theme.colors.primary : theme.colors.border}`,
              borderRadius: theme.borderRadius.medium,
              backgroundColor: agencyAffiliation === 'independent' ? theme.colors.primaryLight : theme.colors.white,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            <div style={{
              fontSize: '16px',
              fontWeight: '500',
              color: theme.colors.textPrimary,
              marginBottom: '4px',
            }}>
              Independent Agent
            </div>
            <div style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
            }}>
              Operating independently
            </div>
          </button>
        </div>
      </Card>

      {agencyAffiliation === 'existing' && (
        <Card>
          <Select
            label="Select Your Agency *"
            value={selectedAgency}
            onChange={(e) => setSelectedAgency(e.target.value)}
            options={[
              { value: '', label: 'Choose an agency...' },
              ...agencies.map(agency => ({ value: agency, label: agency })),
            ]}
            required
          />

          <div style={{ marginTop: '24px' }}>
            <Input
              type="text"
              label="Years with Agency"
              placeholder="5"
              value={yearsWithAgency}
              onChange={(e) => setYearsWithAgency(e.target.value)}
            />
          </div>
        </Card>
      )}

      {agencyAffiliation === 'independent' && (
        <Card>
          <Input
            type="text"
            label="Your Agency/Practice Name *"
            placeholder="Johnson Literary Services"
            value={newAgencyName}
            onChange={(e) => setNewAgencyName(e.target.value)}
            required
          />

          <div style={{ marginTop: '24px' }}>
            <Input
              type="url"
              label="Agency Website"
              placeholder="https://www.youragency.com"
              value={agencyWebsite}
              onChange={(e) => setAgencyWebsite(e.target.value)}
            />
          </div>

          <div style={{ marginTop: '24px' }}>
            <Input
              type="text"
              label="Years in Business"
              placeholder="10"
              value={yearsWithAgency}
              onChange={(e) => setYearsWithAgency(e.target.value)}
            />
          </div>
        </Card>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div>
      <h2 style={{
        fontSize: '28px',
        fontWeight: '500',
        marginBottom: '8px',
        color: theme.colors.textPrimary,
      }}>
        Manuscript Wish List (MSWL)
      </h2>
      <p style={{
        fontSize: '16px',
        color: theme.colors.textSecondary,
        marginBottom: '32px',
      }}>
        Help writers understand what you're looking for
      </p>

      <Card style={{ marginBottom: '24px' }}>
        <label style={{
          fontSize: '14px',
          fontWeight: '500',
          color: theme.colors.textPrimary,
          marginBottom: '12px',
          display: 'block',
        }}>
          Primary Genres * (Select up to 5)
        </label>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
        }}>
          {genres.map((genre) => (
            <button
              key={genre}
              onClick={() => handleGenreToggle(genre)}
              disabled={!primaryGenres.includes(genre) && primaryGenres.length >= 5}
              style={{
                padding: '8px 16px',
                border: `2px solid ${primaryGenres.includes(genre) ? theme.colors.primary : theme.colors.border}`,
                borderRadius: theme.borderRadius.pill,
                backgroundColor: primaryGenres.includes(genre) ? theme.colors.primaryLight : theme.colors.white,
                color: primaryGenres.includes(genre) ? theme.colors.primaryDark : theme.colors.textPrimary,
                cursor: !primaryGenres.includes(genre) && primaryGenres.length >= 5 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s',
                opacity: !primaryGenres.includes(genre) && primaryGenres.length >= 5 ? 0.5 : 1,
              }}
            >
              {genre}
            </button>
          ))}
        </div>
        <div style={{
          fontSize: '13px',
          color: theme.colors.textSecondary,
          marginTop: '8px',
        }}>
          {primaryGenres.length} of 5 selected
        </div>
      </Card>

      <Card>
        <Textarea
          label="What are you looking for? *"
          placeholder="Describe the types of manuscripts you're actively seeking. What themes, voices, or stories excite you? What makes you want to read more?"
          value={mswlDescription}
          onChange={(e) => setMswlDescription(e.target.value)}
          rows={6}
          required
          helperText="This is your main MSWL that writers will see"
        />

        <div style={{ marginTop: '24px' }}>
          <Textarea
            label="Specific Wants (Optional)"
            placeholder="List any specific elements you're particularly interested in right now. For example: 'Strong female protagonists in sci-fi, literary fiction with magical realism elements, diverse voices in YA...'"
            value={specificWants}
            onChange={(e) => setSpecificWants(e.target.value)}
            rows={5}
          />
        </div>

        <div style={{ marginTop: '24px' }}>
          <Textarea
            label="Deal Breakers (Optional)"
            placeholder="What should writers NOT send you? Be clear about genres or topics you don't represent to save everyone time."
            value={dealBreakers}
            onChange={(e) => setDealBreakers(e.target.value)}
            rows={4}
          />
        </div>
      </Card>
    </div>
  );

  const renderStep4 = () => (
    <div>
      <h2 style={{
        fontSize: '28px',
        fontWeight: '500',
        marginBottom: '8px',
        color: theme.colors.textPrimary,
      }}>
        Query Preferences
      </h2>
      <p style={{
        fontSize: '16px',
        color: theme.colors.textSecondary,
        marginBottom: '32px',
      }}>
        Set your submission guidelines and response expectations
      </p>

      <Card style={{ marginBottom: '24px' }}>
        <Select
          label="Typical Response Timeframe *"
          value={responseTimeframe}
          onChange={(e) => setResponseTimeframe(e.target.value)}
          options={[
            { value: '', label: 'Select timeframe...' },
            ...responseTimeframes,
          ]}
          required
          helperText="How long does it typically take you to respond to queries?"
        />

        <div style={{ marginTop: '24px' }}>
          <label style={{
            fontSize: '14px',
            fontWeight: '500',
            color: theme.colors.textPrimary,
            marginBottom: '12px',
            display: 'block',
          }}>
            Query Status *
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '16px',
          }}>
            <button
              onClick={() => setQueryStatus('open')}
              style={{
                padding: '16px',
                border: `2px solid ${queryStatus === 'open' ? theme.colors.success : theme.colors.border}`,
                borderRadius: theme.borderRadius.medium,
                backgroundColor: queryStatus === 'open' ? theme.colors.successLight : theme.colors.white,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <div style={{
                fontSize: '16px',
                fontWeight: '500',
                color: theme.colors.textPrimary,
              }}>
                Open to Queries
              </div>
            </button>

            <button
              onClick={() => setQueryStatus('closed')}
              style={{
                padding: '16px',
                border: `2px solid ${queryStatus === 'closed' ? theme.colors.error : theme.colors.border}`,
                borderRadius: theme.borderRadius.medium,
                backgroundColor: queryStatus === 'closed' ? theme.colors.errorLight : theme.colors.white,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <div style={{
                fontSize: '16px',
                fontWeight: '500',
                color: theme.colors.textPrimary,
              }}>
                Currently Closed
              </div>
            </button>
          </div>
        </div>

        <div style={{ marginTop: '24px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <input
              type="checkbox"
              id="accepting-clients"
              checked={acceptingNewClients}
              onChange={(e) => setAcceptingNewClients(e.target.checked)}
              style={{
                width: '20px',
                height: '20px',
                cursor: 'pointer',
              }}
            />
            <label
              htmlFor="accepting-clients"
              style={{
                fontSize: '14px',
                color: theme.colors.textPrimary,
                cursor: 'pointer',
              }}
            >
              I am currently accepting new clients
            </label>
          </div>
        </div>
      </Card>

      <Card>
        <Textarea
          label="Submission Guidelines"
          placeholder="Describe what you want writers to include in their query. For example: 'Please include a query letter, 1-2 page synopsis, and the first 10 pages of your manuscript in the body of the email. No attachments, please.'"
          value={submissionGuidelines}
          onChange={(e) => setSubmissionGuidelines(e.target.value)}
          rows={6}
          helperText="Be clear about your requirements to get better-quality submissions"
        />
      </Card>
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Header */}
        <div style={{ marginBottom: '40px', textAlign: 'center' }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            Welcome, Literary Agent!
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            Let's set up your agent profile
          </p>
        </div>

        {renderProgressBar()}

        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}

        {/* Navigation Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '40px',
        }}>
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={currentStep === 1}
          >
            ← Back
          </Button>

          <div style={{ display: 'flex', gap: '12px' }}>
            {currentStep < totalSteps && (
              <Button variant="outline" onClick={handleSkip}>
                Skip for Now
              </Button>
            )}
            <Button onClick={handleNext}>
              {currentStep === totalSteps ? 'Complete Setup' : 'Next →'}
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
